<template>
  <Drawer :open="open" swipe-direction="down" @update:open="requestOpen">
    <DrawerContent
      :aria-busy="busy"
      :style="{ '--sheet-viewport-height': viewportHeight + 'px' }"
      class="tw:mx-auto tw:w-full tw:max-w-[var(--app-frame-width)] tw:bg-card tw:shadow-[var(--shadow-sheet)] tw:data-[swipe-direction=down]:rounded-t-[var(--radius-sheet)] tw:data-[swipe-direction=down]:mt-0 tw:data-[swipe-direction=down]:bottom-[var(--drawer-keyboard-inset,0px)] tw:data-[swipe-direction=down]:max-h-[min(calc(var(--sheet-viewport-height)*0.92),calc(var(--sheet-viewport-height)-var(--app-safe-top)-1rem))]"
    >
      <header
        class="sheet__header tw:flex tw:items-center tw:gap-0 tw:justify-between tw:flex-none"
      >
        <div>
          <DrawerTitle class="tw:type-sheet"
            ><slot name="title">{{ title }}</slot></DrawerTitle
          >
          <DrawerDescription
            v-if="description"
            class="tw:mt-2 tw:type-body tw:text-muted-foreground"
            >{{ description }}</DrawerDescription
          >
          <DrawerDescription v-else class="tw:sr-only">{{ title }}</DrawerDescription>
        </div>
        <DrawerClose as-child
          ><Button variant="close" size="icon-close" :disabled="busy" :aria-label="t('nkf.close')"
            ><AppIcon size="action" name="x" /></Button
        ></DrawerClose>
      </header>
      <div
        ref="body"
        :inert="busy"
        class="sheet__body tw:min-h-0 tw:overflow-y-auto tw:type-content"
      >
        <slot />
      </div>
      <AppDialogActions
        v-if="$slots.footer || $slots['secondary-actions']"
        class="sheet__footer tw:flex-none tw:bg-(--card)"
      >
        <template #secondary><slot name="secondary-actions" /></template>
        <slot name="footer" />
      </AppDialogActions>
    </DrawerContent>
  </Drawer>
</template>

<script setup>
// 1. Import necessary components
import { useI18n } from 'vue-i18n';
import { useWindowSize } from '@vueuse/core';
import { ref } from 'vue';
import { useFocusVisibility } from '@/shared/composables/useFocusVisibility';
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription, DrawerClose } from './ui/drawer';
import { Button } from './ui/button';
import AppIcon from './AppIcon.vue';
import AppDialogActions from './AppDialogActions.vue';
// 2. Use
const open = defineModel('open', { type: Boolean, default: false });
const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: '' },
  beforeClose: { type: Function, default: null },
  busy: { type: Boolean, default: false },
});
const { t } = useI18n();
// Constrain the sheet to the visible area above the software keyboard.
const { height: viewportHeight } = useWindowSize({ type: 'visual' });
// 3. Define a reactive variable
const body = ref(null);
// 4. Define methods or computed properties if needed
async function requestOpen(value, details) {
  if (props.busy) {
    details.cancel();
    return;
  }
  if (value || !props.beforeClose) {
    open.value = value;
    return;
  }
  // Keep the drawer in place while its confirmation is shown, including after a swipe.
  details.cancel();
  const confirmed = await props.beforeClose();
  if (confirmed && open.value) {
    open.value = false;
  }
}
// 5. VueJS Hooks
useFocusVisibility(body);
</script>

<style scoped>
.sheet__header {
  padding: 8px calc(var(--page-gutter) + 2px) 10px;
}
.sheet__body {
  overscroll-behavior: contain;
  padding: 4px calc(var(--page-gutter) + 2px) 0;
}
.sheet__body:last-child {
  padding-bottom: max(var(--sheet-footer-gap), var(--app-safe-bottom));
}
.sheet__footer {
  padding: 14px calc(var(--page-gutter) + 2px) max(var(--sheet-footer-gap), var(--app-safe-bottom));
}
</style>
